import { useSuspenseQuery } from '@tanstack/react-query';
import cn from 'classnames';
import { Fragment } from 'react';
import { useConditionalEndpointOIDCUsersQuery } from '#api/endpoint-oidc-users.queries.ts';
import { maxRoutesQueryOptions } from '#api/routes.queries.ts';
import { InlineButton } from '#components/atoms/buttons/inline-button/InlineButton.tsx';
import { DetailCard } from '#components/atoms/cards/detail-card/DetailCard.tsx';
import { Divider } from '#components/atoms/divider/Divider.tsx';
import { DeviceIcon } from '#components/atoms/icons/DeviceIcon.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { IpAddressPill } from '#components/atoms/pill/ip-address-pill/IpAddressPill.tsx';
import { Timestamp } from '#components/atoms/timestamp/Timestamp.tsx';
import { DetailList } from '#components/patterns/detail-list/DetailList.tsx';
import { DetailListRow } from '#components/patterns/detail-list/DetailListRow.tsx';
import { NetworkLink } from '#components/patterns/entity-links/network-link/NetworkLink.tsx';
import { RoleLink } from '#components/patterns/entity-links/role-link/RoleLink.tsx';
import { IpAddressPillList } from '#components/patterns/ip-address-pill-list/IpAddressPillList.tsx';
import type { Host } from '#dn-types/host.ts';
import type { Network } from '#dn-types/network.ts';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { EditableHostTags } from '#screens/hosts/edit-tags/EditableHostTags.tsx';
import { getIpV4 } from '#util/hostIps.ts';
import { osDisplayFromMetadata } from '#util/hostOs.ts';
import { computeHostStatus } from '#util/hostStatus.ts';
import { getCidrV4 } from '#util/networkCidrs.ts';
import { ClientVersion } from '../../hosts-table/ClientVersion.tsx';
import { LastSeen } from './LastSeen.tsx';
import { HostRoutes } from './routes/HostRoutes.tsx';
import styles from './HostHeader.module.css';

type Props = {
  host: Host;
  network: Network;
  dataUpdatedAt: Date;
  className?: string;
  onAddIPv4Click?: () => void;
  /** Allow editing the host's tags in place */
  canEditTags?: boolean;
};

export function HostHeader({ host, network, dataUpdatedAt, className, onAddIPv4Click, canEditTags }: Props) {
  const location = useAppLocation();
  const status = computeHostStatus(host, dataUpdatedAt);
  const os = osDisplayFromMetadata(host.metadata.os);
  const { data: deviceOwner, isFetching: isFetchingDeviceOwner } = useConditionalEndpointOIDCUsersQuery(
    host.endpointOIDCUserID
  );
  const routerForRoutes = useSuspenseQuery({
    ...maxRoutesQueryOptions,
    select({ data }) {
      return { data: data.filter((route) => route.routerHostID === host.id) };
    },
  }).data.data;

  return (
    <DetailCard className={className}>
      <div className={styles.HostHeader_top}>
        <div className="flex">
          <DeviceIcon
            isLighthouse={host.isLighthouse}
            isRelay={host.isRelay}
            platform={host.metadata.platform}
            status={status}
          />
          <div className={styles.HostHeader_title}>
            <p className="h4 mb-4 wrap-anywhere">{host.name}</p>
            <p className="p-md m-0">
              <LastSeen host={host} dataUpdatedAt={dataUpdatedAt} />
            </p>
          </div>
        </div>
      </div>

      <div className={styles.HostHeader_info}>
        <DetailList>
          {host.ipAddresses
            .filter((ip) => ip.includes(':'))
            .map((ip) => (
              <DetailListRow key={ip} label="IPv6 address" value={<IpAddressPill className="-ml-6" ipAddress={ip} />} />
            ))}
          {host.ipAddresses
            .filter((ip) => ip.includes('.'))
            .map((ip) => (
              <DetailListRow key={ip} label="IPv4 address" value={<IpAddressPill className="-ml-6" ipAddress={ip} />} />
            ))}
          {!getIpV4(host) && getCidrV4(network) && onAddIPv4Click ?
            <DetailListRow
              label="IPv4 address"
              value={<InlineButton onClick={onAddIPv4Click}>+ Add IPv4 address</InlineButton>}
            />
          : null}
          <DetailListRow label="Client version" value={<ClientVersion host={host} dataUpdatedAt={dataUpdatedAt} />} />
          {os ?
            <DetailListRow label="Operating system" value={os.label} />
          : null}
          <DetailListRow label="Role" value={host.roleID ? <RoleLink roleID={host.roleID} /> : 'No role'} />
          <DetailListRow label="Listen port" value={host.listenPort === 0 ? 'Dynamic' : host.listenPort} />
        </DetailList>
        <DetailList>
          {routerForRoutes.length ?
            <DetailListRow
              label="Router for"
              value={routerForRoutes.map((route, idx) => (
                <Fragment key={route.id}>
                  <Link className="a" to={`/routes/${route.id}`} state={{ from: location }}>
                    {route.name}
                  </Link>
                  {idx < routerForRoutes.length - 1 ? ', ' : null}
                </Fragment>
              ))}
            />
          : null}
          <HostRoutes tagNames={host.tags} />
          <DetailListRow label="Network" value={<NetworkLink networkId={host.networkID} />} />
          {host.endpointOIDCUserID ?
            <DetailListRow
              label="Device owner"
              value={
                deviceOwner ? deviceOwner.email
                : isFetchingDeviceOwner ?
                  'Loading…'
                : host.endpointOIDCUserID // Fall back to id if we can't find the email
              }
            />
          : null}
          <DetailListRow label="Created" value={<Timestamp datetime={host.createdAt} />} />
          {host.staticAddresses?.length ?
            <DetailListRow label="Static addresses" value={<IpAddressPillList ipAddresses={host.staticAddresses} />} />
          : null}
        </DetailList>
      </div>
      <Divider className={cn('mb-8', styles.HostHeader_divider)} />
      <DetailList>
        <DetailListRow
          label="Tags"
          // Only takes effect below --size-breakpoint-xs, where the row stacks and the label becomes
          // a flex item; it keeps the wrapped pills off the label. Table cells ignore margins.
          labelClassName="mb-8"
          value={<EditableHostTags host={host} canEdit={canEditTags} />}
        />
      </DetailList>
    </DetailCard>
  );
}
